import React, {
  useState,
  useContext,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useForm } from "react-hook-form";
import "../ContentPages/style.css";
import SubmitButton from "../Buttons/SubmitButton";
import BackButton from "../Buttons/BackButton";
import FieldError from "../FieldError";
import { add, debounce } from "lodash-es";
import { AppContext } from "../../contexts/AppContext";
import {
  getAddressDetails,
  getAddressSearch,
  submitApplication,
} from "../../Services/applicationService";
import { getCountries } from "../../Services/metadataService.jsx";
import { verifyArs } from "../../Services/applicationService.jsx";
import Loader from "../Loader/Loader";
import {
  getCountryByLabel,
  getCountryNameById,
  removeAuthToken,
} from "../../Services/localStorage.tsx";
import { useNavigate } from "react-router-dom";
import { updateApplication } from "../../Services/applicationService.jsx";
import { toTitleCase } from "../../Utils/stringHandler.tsx";
import FloatingTextField from "../FormFields/FloatingTextField.tsx";
import FloatingDateField from "../FormFields/FloatingDateField.tsx";
import Calendar from "../../assets/images/Calendar.svg";
import FloatingSelectField from "../FormFields/FloatingSelectField.tsx";
import Checkbox from "../FormFields/Checkbox.tsx";
import Check from "../../assets/images/Check.svg";

interface RefereeFormData {
  givenNames: string;
  familyName: string;
  passportNumber: string;
  dateOfBirth: string;
  yearsKnown: number;
  phone: string;
  email: string;
  country: string;
  addressPhone: string;
  addressSearch: string;
  unitNumber?: string;
  streetName?: string;
  suburb?: string;
  town?: string;
  postcode?: string;
  consent1: boolean;
  consent2: boolean;
}

interface Country {
  dia_countryid: string;
  Name: string;
  Statecode: number;
  "ISO code": string;
}

const RefereeDetails = ({ handleNext, handleBack, handleCancel }) => {
  const navigate = useNavigate();
  const [showRefreePopup, setShowRefreePopup] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const suggestionsRef = useRef<HTMLUListElement>(null);

  const {
    register,
    control,
    handleSubmit,
    setValue,
    setError,
    setFocus,
    watch,
    formState: { errors, isValid },
  } = useForm<RefereeFormData>({ shouldFocusError: true, mode: "onTouched" });

  const handleNoReferee = () => {
    setUserFlows({
      ...userFlows,
      noPhotoId: false,
      refereeNotAvailable: true,
    });
    navigate("/app/photo-id");
  };

  const yearsKnownWatch = watch("yearsKnown");
  const country = watch("country", "");

  const [showManualAddress, setShowManualAddress] = useState(false);
  const yearsKnownOptions = [
    { label: "Less than 1 year", value: "0" },
    ...Array.from({ length: 100 }, (_, i) => ({
      label: `${i + 1}`,
      value: `${i + 1}`,
    })),
  ];

  const [addrQuery, setAddrQuery] = useState("");
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [addrLoading, setAddrLoading] = useState(false);
  const [countries, setCountries] = useState<Country[]>([]);
  const { appState, setAppState, userFlows, setUserFlows } =
    useContext(AppContext);
  const [validating, setValidating] = useState(false);
  const [arsError, setArsError] = useState(false);
  const [dobError, setDobError] = useState(false);

  useEffect(() => {
    if (focusedIndex >= 0 && suggestionsRef?.current) {
      const items = suggestionsRef?.current.querySelectorAll("li");
      items[focusedIndex]?.scrollIntoView({ block: "nearest" });
    }
  }, [focusedIndex]);

  useEffect(() => {
    getCountries()
      .then((meta) => {
        const parsed = JSON.parse(meta.countries || "[]") as Country[];

        let filtered = parsed.filter((c) => c.Statecode === 0);

        let ordered = [
          ...filtered.filter((c) => c.Name === "New Zealand"),
          ...filtered.filter((c) => c.Name !== "New Zealand"),
        ];

        let unique: Country[] = Array.from(
          new Map(ordered.map((c) => [c.dia_countryid, c])).values(),
        );

        setCountries(unique);
      })
      .catch((err) => console.error("Failed to load country list", err));
  }, []);

  useEffect(() => {
    const populateRefereeForm = async () => {
      if (
        appState?.requestPayload?.referee &&
        appState?.requestPayload?.referee?.dateOfBirth !== "9999-01-01"
      ) {
        const referee = appState.requestPayload.referee;
        console.log("Setting referee details from appState:", referee);

        setValue("givenNames", referee.givenNames || "");
        setValue("familyName", referee.familyName || "");
        setValue("passportNumber", referee.travelDocumentNumber || "");
        setValue("dateOfBirth", referee.dateOfBirth || "");
        setValue("yearsKnown", referee.numberOfYearsKnownFor || 0);
        setValue("email", referee.emailAddress || "");
        setValue("phone", referee.mobilePhone || "");

        // Await country name

        const countryName = await getCountryNameById(referee.addressCountryId);

        setValue("country", countryName || "");

        // Manual address fields if present
        const streetParts = (referee.addressStreet || "").split(" ");

        setValue("unitNumber", streetParts[0] || "");
        setValue("streetName", streetParts.slice(1).join(" ") || "");
        setValue("suburb", referee.addressSuburb || "");
        setValue("town", referee.addressCity || "");
        setValue("postcode", referee.addressPostcode || "");

        // Optional addressSearch display value
        const addrSearchValue = [
          referee.addressStreet,
          referee.addressSuburb,
          referee.addressCity,
        ]
          .filter(Boolean)
          .join(", ");
        console.log("Setting addressSearch value:", addrSearchValue);
        setValue("addressSearch", addrSearchValue);
      }
    };

    populateRefereeForm();
  }, [setValue]);

  /**
   * Returns `true` if the person is younger than 16.
   * `dob` can be a Date, ISO-8601 string (yyyy-mm-dd) or any value that
   * `new Date(dob)` can parse.
   */
  const isUnder16 = (dob: string): boolean => {
    const birth = new Date(dob);
    const today = new Date();

    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    const dayDiff = today.getDate() - birth.getDate();

    // If birthday hasn’t happened yet this year, subtract 1
    if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
      age--;
    }
    return age < 16;
  };

  const updateApp = async (data: RefereeFormData) => {
    const countryOfBirthId = await getCountryByLabel(data.country);
    let updatedAppState = { ...appState };
    let visitAA = false;
    // if (
    //   (appState?.requestPayload?.eventTypeSearch?.[1]?.eventTypeId ===
    //     "b3cbe24e-a934-ed11-9db1-0022489412bc" &&
    //     new Date(
    //       appState?.requestPayload?.eventTypeSearch?.[1]?.dateOfEvent
    //     ).setHours(0, 0, 0, 0) < new Date("2009-01-01").setHours(0, 0, 0, 0)) ||
    //   appState?.requestPayload?.photoShop?.flag === true
    // ) {
    //   visitAA = true;
    //   updatedAppState = {
    //     ...appState,
    //     requestPayload: {
    //       ...appState.requestPayload,
    //       photoShop: {
    //         flag: true,
    //       },
    //     },
    //   };
    // }

    console.log("Country of Birth ID:", countryOfBirthId);
    updatedAppState = {
      ...appState,
      requestPayload: {
        ...appState.requestPayload,
        referee: {
          familyName: toTitleCase(data.familyName),
          givenNames: toTitleCase(data.givenNames),
          dateOfBirth: data.dateOfBirth,
          numberOfYearsKnownFor: data.yearsKnown,
          travelDocumentNumber: data.passportNumber,
          emailAddress: data.email,
          mobilePhone: data.phone,
          addressStreet: data.unitNumber + " " + data.streetName,
          addressSuburb: data.suburb,
          addressPostcode: data.postcode,
          addressCity: data.town,
          addressCountryId: countryOfBirthId?.dia_countryid || "",
        },
        hasRefereeData: true,
        ...(!appState?.requestPayload?.liveness?.livenessResult && {
          photoShop: { flag: true },
        }),
      },
    };
    // console.log("Updated Application State:", updatedAppState);
    if (updatedAppState.requestPayload.applicationId) {
      updateApplication(updatedAppState.requestPayload)
        .then((response) => {
          console.log("Application updated successfully:", response);
          setAppState(updatedAppState);

          if (userFlows.childApplication) {
            navigate("/app/application-at-store");
            return;
          }

          if (visitAA) {
            navigate("/app/application-at-store");
            return;
          }

          if (
            appState?.requestPayload?.photoShop?.flag ||
            updatedAppState?.requestPayload?.photoShop?.flag
          ) {
            navigate("/app/application-at-store");
            return;
          }

          submitApplication({
            serviceId: appState.requestPayload.applicationId,
          })
            .then((resp) => {
              console.log("Application submitted successfully:", resp);

              handleNext();
              setValidating(false);
            })
            .catch((error) => {
              console.error("Error submitting application:", error);
              setValidating(false);
            });
        })
        .catch((error) => {
          console.error("Error initiating application:", error);
          setValidating(false);
        });
    }
  };
  const saveApplication = async (data: RefereeFormData) => {
    setValidating(true);
    setArsError(false);
    console.log("Verifying ARS for data:", data);

    if (
      appState?.requestPayload?.eventTypeSearch[0]?.documentNumber ===
      data.passportNumber
    ) {
      setError("passportNumber", {
        type: "manual",
        message:
          "You cannot enter your own details. Enter your referee's NZ passport number.",
      });
      setFocus("passportNumber");
      setValidating(false);
      return;
    }

    if (appState?.requestPayload?.applicant?.emailAddress === data.email) {
      setError("email", {
        type: "manual",
        message:
          "You cannot enter your own details. Enter your referee's email address.",
      });
      setFocus("email");
      setValidating(false);
      return;
    }

    //isUnder16(data.dateOfBirth) ? setDobError(true) : setDobError(false);
    if (isUnder16(data.dateOfBirth)) {
      setDobError(true);
      setFocus("dateOfBirth");
      setValidating(false);
      return;
    } else {
      setDobError(false);
    }
    try {
      const vFormData = {
        arsId: data.passportNumber,
        register: "passport",
        givenName: data.givenNames,
        familyName: data.familyName,
        dateOfBirth: data.dateOfBirth,
        referee: true,
      };

      const resp = await verifyArs(vFormData, navigate);
      if (resp) {
        if (
          !resp.ok &&
          resp.error === "Maximum identity verification attempts exceeded."
        ) {
          console.error("Maximum identity verification attempts exceeded.");
          removeAuthToken();
          navigate("/app/attempts-exhausted");
          setValidating(false);
          return;
        }
        if (resp.error) {
          console.error("ARS verification failed:", resp);
          setValidating(false);
          setArsError(true);
          return;
        }
      }
      updateApp(data);
    } catch (err) {
      console.error("ARS verification error:", err);
      setValidating(false);
      setArsError(true);
      return;
    }
  };

  const searchAddresses = useMemo(
    () =>
      debounce(async (q: string) => {
        if (q.length < 3) {
          setSuggestions([]);
          setAddrLoading(false);
          return;
        }
        setAddrLoading(true);
        try {
          const resp = await getAddressSearch(q);
          setSuggestions(resp?.addresses || []);
        } catch (err) {
          console.error("Address search failed:", err);
          setSuggestions([]);
        } finally {
          setAddrLoading(false);
        }
      }, 400),
    [],
  );

  const selectAddress = useCallback(
    async (sug: any) => {
      setAddrQuery(sug.FullAddress);
      setSuggestions([]);
      setFocusedIndex(-1);

      try {
        const full = await getAddressDetails(sug.DPID);
        console.log("Full address details:", full.details[0]);
        const add = full.details[0];
        setValue("unitNumber", add.StreetNumber || "");
        setValue("streetName", add.RoadName || "");
        setValue("suburb", add.Suburb || "");
        setValue("town", add.CityTown || "");
        setValue("postcode", add.Postcode || "");
      } catch (err) {
        console.error("Failed to get address detail:", err);
      }
    },
    [setValue],
  );

  const handleAddressKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (suggestions.length === 0) return;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setFocusedIndex((prev) =>
            prev < suggestions.length - 1 ? prev + 1 : prev,
          );
          break;
        case "ArrowUp":
          e.preventDefault();
          setFocusedIndex((prev) => (prev > 0 ? prev - 1 : 0));
          break;
        case "Enter":
          e.preventDefault();
          if (focusedIndex >= 0 && focusedIndex < suggestions.length) {
            selectAddress(suggestions[focusedIndex]);
          }
          break;
        case "Escape":
          setSuggestions([]);
          setFocusedIndex(-1);
          break;
      }
    },
    [suggestions, focusedIndex, selectAddress],
  );

  return (
    <>
      {validating && <Loader />}
      <form onSubmit={handleSubmit(saveApplication)}>
        <div className="intro">
          <h1 className="intro-title-mi" lang="mi-NZ">
            Ngā taipitopito a tō kaitautoko tuakiri
          </h1>
          <hr />
          <h1 className="intro-title">Your identity referee details</h1>

          {/* ─── Referee passport details ───────────────────────── */}
          <div className="step-container">
            <h2 className="text-24 mb-16">
              Your identity referee's New Zealand passport details
            </h2>
            <p className="mb-16">
              Enter the following details exactly as they appear on the
              referee's passport.
            </p>

            {/* <div
              className={`field-wrapper mb-3 ${
                errors.givenNames ? "error-field" : ""
              }`}
            >
              <label className="field-label">Given name(s)</label>
              <input
                type="text"
                className="common-field"
                {...register("givenNames")}
              />
            </div>
            <FieldError error={errors.givenNames} info={null} /> */}
            <FloatingTextField
              name="givenNames"
              label="Given name(s)"
              inputProps={register("givenNames", {
                validate: (value) => {
                  console.log("Validate called for givenName:", value);
                  return (
                    !value ||
                    value?.trim().length < 199 ||
                    "Given name(s) must be less than 198 characters."
                  );
                },
              })}
              error={errors.givenNames}
            />

            <FloatingTextField
              name="surname"
              label="Surname or family name"
              required
              inputProps={register("familyName", {
                required:
                  "If you have one name, enter it as your surname or family name.",
                validate: (value) => {
                  console.log("Validate called for givenName:", value);
                  return (
                    !value ||
                    value?.trim().length < 85 ||
                    "Surname or family name must be less than 84 characters."
                  );
                },
              })}
              error={errors.familyName}
            />

            {/* <div
              className={`field-wrapper mb-3 ${
                errors.passportNumber ? "error-field" : ""
              }`}
            >
              <label className="field-label">
                New Zealand passport number<span className="req-text">*</span>
              </label>
              <input
                type="text"
                className="common-field"
                {...register("passportNumber", {
                  required: "This field is required",
                })}
              />
            </div>
            <FieldError error={errors.passportNumber} info={null} /> */}
            <FloatingTextField
              name="passportNumber"
              label="NZ passport number"
              required
              inputProps={register("passportNumber", {
                required: "This field is required",
                pattern: {
                  value: /^[A-Za-z]{1,2}\d{6}$/,
                  message: "Invalid NZ passport number format",
                },
              })}
              error={errors.passportNumber}
            />
            {/* {
              arsError && (
                <FieldError error={{"message":"This is not a valid NZ passport number. Check the details and try again."}} info={null} />
              )
            } */}
            {/* <div
              className={`field-wrapper mb-3 ${
                errors.dateOfBirth ? "error-field" : ""
              }`}
            >
              <label className="field-label">
                Date of birth<span className="req-text">*</span>
              </label>
              <input
                type="date"
                className="common-field"
                {...register("dateOfBirth", {
                  required: "This field is required",
                  validate: (value) => {
                    if (!value) return "This field is required";

                    const parts = value.split("-");
                    if (parts.length !== 3 || parts[0].length !== 4) {
                      return "Year must be 4 digits";
                    }

                    const date = new Date(value);
                    return !isNaN(date.getTime()) || "Invalid date";
                  },
                })}
              />
            </div> */}
            <FloatingDateField
              name="dateOfBirth"
              label="Date of birth (dd/mm/yyyy)"
              control={control}
              calendarIcon={Calendar}
              required
              error={errors.dateOfBirth}
            />

            {arsError && (
              <FieldError
                error={{
                  message:
                    "We could not verify your referee's passport details. Please check their details and try again.",
                }}
                info={null}
              />
            )}
            {dobError && (
              <FieldError
                error={{
                  message: "The referee must be aged 16 years or over.",
                }}
                info={null}
              />
            )}
          </div>

          {/* ─── Years known ────────────────────────────────────── */}
          <div className="step-container">
            <h2 className="step-title mb-2">
              How many years has your identity referee known you for?
            </h2>

            {/* <div
              className={`field-wrapper mb-3 ${
                errors.yearsKnown ? "error-field" : ""
              }`}
            >
              <select
                className="common-field"
                defaultValue=""
                {...register("yearsKnown", {
                  required: "This field is required",
                  validate: (v) => {
                    if (v === "") return "Please choose a value";
                    if (v === "0")
                      return "The referee must have known the applicant for at least 1 year. Please use an alternative referee.";
                    return true;
                  },
                })}
              >
                <option value="" disabled>
                  Select years
                </option>
                {yearsKnownOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <FieldError error={errors.yearsKnown} info={null} /> */}
            <FloatingSelectField
              name="yearsKnown"
              label="Number of years"
              options={yearsKnownOptions}
              register={register}
              registerOptions={{
                required: "This field is required",
                validate: (value) =>
                  !value ||
                  value !== "0" ||
                  "The referee must have known the applicant for at least 1 year. Please use an alternative referee.",
              }}
              value={yearsKnownWatch}
              required
              error={errors.yearsKnown}
            />
          </div>

          {/* ─── Contact details ────────────────────────────────── */}
          <div className="step-container">
            <h2 className="step-title mb-2">
              Your identity referee's contact details
            </h2>

            {/* <div
              className={`field-wrapper mb-3 ${
                errors.phone ? "error-field" : ""
              }`}
            >
              <label className="field-label">
                Mobile number<span className="req-text">*</span>
              </label>
              <input
                type="tel"
                className="common-field"
                {...register("phone", { required: "This field is required" })}
              />
            </div>
            <FieldError error={errors.phone} info={null} /> */}
            <FloatingTextField
              name="phone"
              label="Mobile number"
              required
              inputProps={register("phone", {
                required: "This field is required",
                pattern: {
                  value: /^\+?[0-9\s-]{7,12}$/,
                  message: "Invalid phone number format",
                },
              })}
              error={errors.phone}
            />

            {/* <div
              className={`field-wrapper mb-3 ${
                errors.email ? "error-field" : ""
              }`}
            >
              <label className="field-label">
                Email address<span className="req-text">*</span>
              </label>
              <input
                type="email"
                className="common-field"
                {...register("email", {
                  required: "This field is required",
                  pattern: {
                    value: /\S+@\S+\.\S+/,
                    message: "Invalid email format",
                  },
                })}
              />
            </div>
            <FieldError error={errors.email} info={null} /> */}
            <FloatingTextField
              name="email"
              label="Email address"
              required
              inputProps={register("email", {
                required: "This field is required",
                validate: (value) => {
                  console.log("Validate called for givenName:", value);
                  return (
                    !value ||
                    value?.trim().length < 100 ||
                    "Email address must be less than 100 characters."
                  );
                },
                pattern: {
                  value: /\S+@\S+\.\S+/,
                  message: "Invalid email format",
                },
              })}
              error={errors.email}
            />
            <div style={{ marginBottom: 40 }} />

            {/* <div
              className={`field-wrapper mb-3 ${
                errors.country ? "error-field" : ""
              }`}
            >
              <label className="field-label">
                Address country<span className="req-text">*</span>
              </label>
              <select
                className="common-field"
                {...register("country", { required: true })}
              >
                <option value=""></option>
                {countries.map((c) => (
                  <option key={c.dia_countryid} value={c.Name}>
                    {c.Name}
                  </option>
                ))}
              </select>
            </div>
            <FieldError error={errors.country} info={null} /> */}
            <FloatingSelectField
              name="country"
              label="Address country"
              register={register}
              options={countries.map((c) => ({
                value: c.dia_countryid,
                label: c.Name,
              }))}
              error={errors.country}
              required
              value={watch("country") || ""}
            />
            {!showManualAddress ? (
              <>
                <div
                  className={`floating-field field-wrapper mb-16 ${
                    addrQuery ? "has-value" : ""
                  } ${errors.addressSearch ? "error-field" : ""}`}
                >
                  <input
                    type="text"
                    placeholder="Address search"
                    className="common-field"
                    value={addrQuery}
                    {...register("addressSearch", {
                      required: "This field is required",
                    })}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      setAddrQuery(newValue);
                      setValue("addressSearch", newValue);
                      setFocusedIndex(-1);

                      // Clear suggestions immediately if input is too short
                      if (newValue.length < 3) {
                        setSuggestions([]);
                        setAddrLoading(false);
                      } else {
                        searchAddresses(newValue);
                      }
                    }}
                    onKeyDown={handleAddressKeyDown}
                    autoComplete="off"
                    name="addressSearch"
                    role="combobox"
                    aria-autocomplete="list"
                    aria-controls="address-suggestions"
                    aria-expanded={suggestions.length > 0}
                    aria-activedescendant={
                      focusedIndex >= 0
                        ? `suggestion-${focusedIndex}`
                        : undefined
                    }
                  />
                  <label htmlFor="addressSearch" className="field-label">
                    Address search<span className="req-text">*</span>
                  </label>

                  {/* suggestions dropdown */}
                  {/* suggestions dropdown */}
                  {addrQuery && suggestions.length > 0 && (
                    <ul
                      key={suggestions.map((s) => s.DPID).join("-")}
                      className="addr-suggest-list"
                      ref={suggestionsRef}
                      role="listbox"
                      id="address-suggestions"
                    >
                      {suggestions.map((sug, index) => (
                        <li
                          key={sug.DPID}
                          id={`suggestion-${index}`}
                          role="option"
                          aria-selected={index === focusedIndex}
                          className={index === focusedIndex ? "focused" : ""}
                          onClick={() => selectAddress(sug)}
                          onMouseEnter={() => setFocusedIndex(index)}
                        >
                          {sug.FullAddress}
                        </li>
                      ))}
                    </ul>
                  )}
                  {addrLoading && suggestions.length === 0 && (
                    <ul className="addr-suggest-list">
                      <li className="loading">Searching…</li>
                    </ul>
                  )}
                </div>
                <FieldError error={errors.addressSearch} info={null} />
                <div className="mb-3">
                  <label className="terms-label">
                    <a
                      href="#"
                      className="cancel-link"
                      onClick={(e) => {
                        e.preventDefault();
                        setShowManualAddress(true);
                      }}
                    >
                      Enter address manually
                    </a>
                  </label>
                </div>
              </>
            ) : (
              <>
                <div className="mb-3">
                  <label className="terms-label">
                    <a
                      href="#"
                      className="cancel-link"
                      onClick={(e) => {
                        e.preventDefault();
                        setShowManualAddress(false);
                      }}
                    >
                      Search for address
                    </a>
                  </label>
                </div>

                <FloatingTextField
                  name="unitNumber"
                  label="Unit / house number"
                  inputProps={register("unitNumber", {
                    validate: (value) => {
                      return (
                        !value ||
                        value?.trim().length < 101 ||
                        "Unit / house number must be less than 100 characters."
                      );
                    },
                  })}
                  error={errors.unitNumber}
                />

                <FloatingTextField
                  name="streetName"
                  label="Street name"
                  inputProps={register("streetName", {
                    validate: (value) => {
                      return (
                        !value ||
                        value?.trim().length < 101 ||
                        "Street name must be less than 100 characters."
                      );
                    },
                  })}
                  error={errors.streetName}
                />

                <FloatingTextField
                  name="suburb"
                  label="Suburb"
                  inputProps={register("suburb", {
                    validate: (value) => {
                      return (
                        !value ||
                        value?.trim().length < 101 ||
                        "Suburb must be less than 100 characters."
                      );
                    },
                  })}
                  error={errors.suburb}
                />

                <FloatingTextField
                  name="town"
                  label="Town"
                  inputProps={register("town", {
                    validate: (value) => {
                      return (
                        !value ||
                        value?.trim().length < 101 ||
                        "Town must be less than 100 characters."
                      );
                    },
                  })}
                  error={errors.town}
                />

                <FloatingTextField
                  name="postcode"
                  label="Postcode"
                  inputProps={register("postcode", {
                    validate: (value) => {
                      return (
                        !value ||
                        value?.trim().length < 21 ||
                        "Postcode must be less than 21 characters."
                      );
                    },
                  })}
                  error={errors.postcode}
                />
              </>
            )}
          </div>

          <Checkbox
            name="consent1"
            label="The person whose details I have provided has agreed to act as my identity referee."
            register={register}
            required="You must agree to this consent to proceed."
            labelClass="referee-consent"
          />

          {/* <div className="checkbox-wrapper mb-5">
            <input
              type="checkbox"
              id="consent2"
              className="terms-check"
              {...register("consent2", { required: true })}
            />
            <label htmlFor="consent2" className="terms-label">
              The referee is aware the Department may contact them to verify the
              applicant's identity, and the applicant authorises the Department
              to share details from their application as part of this
              verification process.
            </label>
            <FieldError error={errors.consent2} info={null} />
          </div> */}

          <Checkbox
            name="consent2"
            label="The identity referee is aware the Department of Internal Affairs (DIA) may contact them to verify my identity, and I authorise DIA to share details from my application as part of this verification process."
            register={register}
            required="You must agree to this consent to proceed."
            style={{ maxWidth: 700 }}
            labelClass="referee-consent"
          />
        </div>
        <div className="spacer-64" />

        {/* ─── Buttons ─────────────────────────────────────────── */}
        <div
          className="step-button-container"
          style={{ marginBottom: 24, paddingBottom: 0 }}
        >
          <SubmitButton
            type="submit"
            text="Submit"
            onClick={() => {}}
            disabled={!isValid}
            Icon={Check}
          />
          <BackButton text="Back" onClick={() => navigate(-1)} />
        </div>

        <div className="checkbox-wrapper mb-40">
          <label className="terms-label">
            If you do not know anyone who can be a referee, you can&nbsp;
            <a href="#" className="cancel-link" onClick={handleNoReferee}>
              complete your <br />
              application in person at a RealMe partner store.
            </a>
          </label>
        </div>
      </form>
    </>
  );
};

export default RefereeDetails;
